import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../services/apiClient';
import { getAllFeedback, deleteFeedback, FeedbackDto } from '../services/feedback';
import { isCurrentUserAdmin } from '../services/users';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Rating from '@mui/material/Rating';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FeedbackIcon from '@mui/icons-material/Feedback';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

function getAverageRating(feedbacks: FeedbackDto[]): number {
  if (feedbacks.length === 0) return 0;
  const sum = feedbacks.reduce((acc, f) => acc + f.rating, 0);
  return sum / feedbacks.length;
}

function getRatingDistribution(feedbacks: FeedbackDto[]): Record<number, number> {
  const distribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  feedbacks.forEach(f => {
    distribution[f.rating]++;
  });
  return distribution;
}

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState<FeedbackDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<FeedbackDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [feedbackToView, setFeedbackToView] = useState<FeedbackDto | null>(null);

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }

    if (!isCurrentUserAdmin()) {
      setError('Přístup odepřen. Tato stránka je pouze pro administrátory.');
      setLoading(false);
      return;
    }

    const fetchFeedback = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllFeedback();
        
        const normalizedFeedbacks = Array.isArray(data)
          ? data
          : Array.isArray((data as { $values?: FeedbackDto[] })?.$values)
            ? (data as { $values?: FeedbackDto[] }).$values!
            : [];

        setFeedbacks(normalizedFeedbacks);
      } catch (err: unknown) {
        console.error('Error fetching feedback:', err);
        const error = err as { status?: number };
        if (error.status === 403) {
          setError('Přístup odepřen. Nemáte oprávnění pro tuto akci.');
        } else {
          setError('Nepodařilo se načíst zpětnou vazbu. Zkuste to prosím znovu.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [navigate]);

  const handleDeleteClick = (feedback: FeedbackDto) => {
    setFeedbackToDelete(feedback);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteFeedback(feedbackToDelete.id);
      setFeedbacks(feedbacks.filter(f => f.id !== feedbackToDelete.id));
      setDeleteDialogOpen(false);
      setFeedbackToDelete(null);
    } catch (err: unknown) {
      console.error('Error deleting feedback:', err);
      setDeleteError('Nepodařilo se smazat zpětnou vazbu.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFeedbackToDelete(null);
    setDeleteError(null);
  };

  const handleViewClick = (feedback: FeedbackDto) => {
    setFeedbackToView(feedback);
    setViewDialogOpen(true);
  };

  const handleViewClose = () => {
    setViewDialogOpen(false);
    setFeedbackToView(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const averageRating = getAverageRating(feedbacks);
  const ratingDistribution = getRatingDistribution(feedbacks);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <FeedbackIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            Zpětná vazba
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Přehled zpětné vazby od uživatelů
          </Typography>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Celkem zpětných vazeb
            </Typography>
            <Typography variant="h4">{feedbacks.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Průměrné hodnocení
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4">{averageRating.toFixed(1)}</Typography>
              <Rating value={averageRating} precision={0.1} readOnly />
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 250 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Distribuce hodnocení
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[5, 4, 3, 2, 1].map(star => (
                <Chip
                  key={star}
                  label={`${star}★: ${ratingDistribution[star]}`}
                  size="small"
                  color={star >= 4 ? 'success' : star === 3 ? 'warning' : 'error'}
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Hodnocení</TableCell>
              <TableCell>Zpráva</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell align="center">Akce</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbacks.map((feedback) => (
              <TableRow key={feedback.id} hover>
                <TableCell>
                  <Rating value={feedback.rating} readOnly size="small" />
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {feedback.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  {feedback.userEmail || (
                    <Typography variant="body2" color="text.secondary">
                      {feedback.userId ? 'Přihlášený uživatel' : '-'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatDate(feedback.created)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Zobrazit detail">
                    <IconButton
                      color="primary"
                      onClick={() => handleViewClick(feedback)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Smazat">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(feedback)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {feedbacks.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Zatím nebyla přijata žádná zpětná vazba.
        </Alert>
      )}

      {/* View Feedback Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleViewClose} maxWidth="sm" fullWidth>
        <DialogTitle>Detail zpětné vazby</DialogTitle>
        <DialogContent>
          {feedbackToView && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Hodnocení
                </Typography>
                <Rating value={feedbackToView.rating} readOnly />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Zpráva
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {feedbackToView.message}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  E-mail
                </Typography>
                <Typography variant="body1">
                  {feedbackToView.userEmail || '-'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  ID uživatele
                </Typography>
                <Typography variant="body1">
                  {feedbackToView.userId || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Datum odeslání
                </Typography>
                <Typography variant="body1">
                  {formatDate(feedbackToView.created)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Zavřít</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Smazat zpětnou vazbu</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Opravdu chcete smazat tuto zpětnou vazbu?
            <br />
            Tato akce je nevratná.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Zrušit
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Smazat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
